import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useData } from '../../contexts/DataContext';
import { feeApi, studentApi, whatsappAnalyticsApi } from '../../api/services'; 
import { 
  CalendarDays,
  Plus,
  AlertCircle,
  Search,
  DollarSign,
  Send
} from 'lucide-react';
import FeeReminders from '../FeeReminders/FeeReminders';
import FeeStats from './components/FeeStats';
import FeeStructureList from './components/FeeStructureList';
import FeeRecords from './components/FeeRecords';
import RecipientSelector from '../../components/Common/RecipientSelector';

// Utility component for confirmation dialogs
const ConfirmDialog = ({ isOpen, onClose, onConfirm, title, message, children, confirmText = "Confirm", confirmButtonClass = "btn-primary" }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-75 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded shadow-xl max-w-sm w-full">
        <div className="p-3">
          <div className="flex items-center mb-2">
            <div className="p-1 bg-red-100 rounded">
              <AlertCircle className="h-4 w-4 text-red-600" />
            </div>
            <h3 className="text-sm font-bold text-gray-900 ml-2">{title}</h3>
          </div>
          <p className="text-xs text-gray-600 mb-3">{message}</p>
          {children && <div className="mb-4">{children}</div>}
          <div className="flex items-center justify-end space-x-2">
            <button
              onClick={onClose}
              className="px-2 py-1 text-[10px] font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              className={`px-2 py-1 text-[10px] font-medium text-white rounded transition-colors ${confirmButtonClass}`}
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Fees = () => {
  const { students: contextStudents, classes: allClasses, currentAcademicYear, organization } = useData();
  const [localStudents, setLocalStudents] = useState([]);
  
  // Debounced search for Fees
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Merge context students and local students
  const students = useMemo(() => {
      const all = [...contextStudents, ...localStudents];
      // Deduplicate by _id
      const seen = new Set();
      return all.filter(s => {
          if (seen.has(s._id)) return false;
          seen.add(s._id);
          return true;
      });
  }, [contextStudents, localStudents]);

  const [activeSection, setActiveSection] = useState('overview');
  const [showStructureForm, setShowStructureForm] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [editingStructure, setEditingStructure] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [structureToDelete, setStructureToDelete] = useState(null);
  const [preSelectedClassId, setPreSelectedClassId] = useState(null);

  const [sendingReminders, setSendingReminders] = useState(null); // Track which structure is sending
  const [showReminderConfirm, setShowReminderConfirm] = useState(false);
  const [remindingStructureId, setRemindingStructureId] = useState(null);
  const [targetAudience, setTargetAudience] = useState(() => {
    const orgType = localStorage.getItem('organizationType')?.toUpperCase();
    return orgType === 'COLLEGE' ? 'both' : 'parent';
  });

  // Fee Structures
  const [feeStructures, setFeeStructures] = useState([]);
  const [structuresLoading, setStructuresLoading] = useState(false);

  // Student Fee Records
  const [studentFeeRecords, setStudentFeeRecords] = useState([]);
  const [recordsLoading, setRecordsLoading] = useState(false);
  
  // Records by Class state (used in FeeRecords)
  const [expandedClasses, setExpandedClasses] = useState(new Set());
  const [classFilters, setClassFilters] = useState({
    classId: 'all',
    status: 'all',
    search: ''
  });

  // Stats
  const [feeStats, setFeeStats] = useState({ 
    total: 0, 
    pending: 0, 
    overdue: 0, 
    paid: 0, 
    totalAmount: 0, 
    totalPaid: 0, 
    balance: 0,
    partial: 0,
  });
  const [statsLoading, setStatsLoading] = useState(false);
  const [timelineData, setTimelineData] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [campaignStats, setCampaignStats] = useState(null);
  const [campaignLoading, setCampaignLoading] = useState(false);

  // Utility functions
  const formatCurrency = (num) => `₹${Number(num || 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

  const getCategoryStatus = (category) => {
    // This function might be needed by FeeRecords if it's passed down, 
    // but FeeRecords imports RecordsByClassSection which might have its own logic or need this.
    // Keeping it here to pass down.
    if (category.status === 'Paid') return { color: 'bg-green-100 text-green-800', icon: 'CheckCircle' };
    if (category.status === 'Partial') return { color: 'bg-yellow-100 text-yellow-800', icon: 'Clock' };
    
    const dueDate = category.dueDate ? new Date(category.dueDate) : null;
    const isOverdue = category.status === 'Due' && dueDate && dueDate < new Date();
    
    if (isOverdue) return { color: 'bg-red-100 text-red-800', icon: 'AlertTriangle' };
    return { color: 'bg-gray-100 text-gray-800', icon: 'Clock' }; 
  };

  // API Calls
  const loadFeeRecords = async () => {
    setRecordsLoading(true);
    try {
      // Use getAllRecords endpoint with filters
      const params = {
          academicYear: currentAcademicYear,
          status: classFilters.status !== 'all' ? classFilters.status : undefined,
          classId: classFilters.classId !== 'all' ? classFilters.classId : undefined,
          search: debouncedSearch
      };

      const res = await feeApi.getAllRecords(params);
      
      if (res?.data?.length > 0) {
      }
      
      if (res?.success) {
        const records = res.data || [];
        // Sort records by academic year descending to ensure latest year is first
        records.sort((a, b) => {
          const yearA = a.academicYear || '';
          const yearB = b.academicYear || '';
          return yearB.localeCompare(yearA);
        });
        setStudentFeeRecords(records);
        // Extract students from records to ensure they are visible even before class expansion
        const studentsFromRecords = records.map(r => {
            if (!r.studentId || typeof r.studentId !== 'object') return null;
            return {
                ...r.studentId,
                enrollment: r.studentEnrollmentId,
                unitId: r.studentEnrollmentId?.unitId
            };
        }).filter(s => s && s._id);

        if (studentsFromRecords.length > 0) {
            setLocalStudents(prev => {
                const all = [...prev, ...studentsFromRecords];
                const seen = new Set(prev.map(s => s._id));
                const uniqueNew = studentsFromRecords.filter(s => !seen.has(s._id));
                return [...prev, ...uniqueNew];
            });
        }
      } else {
        toast.error(res?.message || 'Failed to load fee records');
        setStudentFeeRecords([]);
      }
    } catch (e) {
      toast.error('Failed to load fee records: ' + (e.message || 'Unknown error'));
      setStudentFeeRecords([]);
    } finally {
      setRecordsLoading(false);
    }
  };

  const loadFeeStructures = async () => {
    setStructuresLoading(true);
    try {
      const res = await feeApi.getFeeStructures({});
      if (res.success) {
        const structures = res.data || [];
        // Sort structures by academic year descending
        structures.sort((a, b) => {
          const yearA = a.academicYear || '';
          const yearB = b.academicYear || '';
          return yearB.localeCompare(yearA);
        });
        setFeeStructures(structures);
      }
    } catch (e) {
      toast.error('Failed to load fee structures');
    } finally {
      setStructuresLoading(false);
    }
  };

  const fetchStudentsForClasses = async (classIds, academicYear) => {
    const yearToUse = academicYear || currentAcademicYear;
    
    // We need to fetch students for each class. 
    // If a class has sections, we must fetch for each section individually.
    const promises = [];

    classIds.forEach(classId => {
        const cls = allClasses.find(c => c._id === classId);
        if (cls && cls.sections && cls.sections.length > 0) {
            // Fetch for each section
            cls.sections.forEach(sec => {
                 // Check common ID patterns
                 const sectionId = sec._id || sec.id;
                 promises.push(studentApi.getStudentsByUnit(classId, yearToUse, sectionId));
            });
        } else {
            // Fetch for class only (if no sections defined)
            promises.push(studentApi.getStudentsByUnit(classId, yearToUse));
        }
    });

    try {
        const results = await Promise.all(promises);
        const newStudents = results.flatMap(res => res.success ? res.data : []);
        
        setLocalStudents(prev => {
            const all = [...prev, ...newStudents];
            const seen = new Set();
            return all.filter(s => {
                if (seen.has(s._id)) return false;
                seen.add(s._id);
                return true;
            });
        });
        return newStudents;
    } catch (e) {
        toast.error("Failed to load students for selected classes");
        return [];
    }
  };

  const loadStats = async () => {
      setStatsLoading(true);
      try {
        const res = await feeApi.getFeeStatistics({});
        if (res?.success) {
          let statsData = {};
          
          // Check if data is an array (raw records) or object (pre-calculated)
          if (Array.isArray(res.data)) {
            const records = res.data;
            const totalRecords = records.length;
            let totalExpected = 0;
            let totalCollected = 0;
            let totalPending = 0;
            let fullyPaidCount = 0;
            let partiallyPaidCount = 0;
            let unpaidCount = 0;
            let overdueCount = 0;

            const now = new Date();

            records.forEach(record => {
              const amount = record.totalAmount || 0;
              const paid = record.totalPaid || 0;
              const balance = record.balance !== undefined ? record.balance : (amount - paid);

              totalExpected += amount;
              totalCollected += paid;
              totalPending += balance;

              if (balance <= 0 && paid > 0) {
                fullyPaidCount++;
              } else if (paid > 0 && balance > 0) {
                partiallyPaidCount++;
              } else {
                unpaidCount++;
              }

              // Check overdue
              if (record.breakdown && Array.isArray(record.breakdown)) {
                 const isOverdue = record.breakdown.some(item => {
                    if (!item.dueDate || item.status === 'Paid') return false;
                    const dueDate = new Date(item.dueDate);
                    // Consider overdue if due date is strictly before today (or customize logic)
                    return dueDate < now;
                 });
                 if (isOverdue) overdueCount++;
              }
            });

            const collectionRate = totalExpected > 0 ? (totalCollected / totalExpected) * 100 : 0;

            statsData = {
              total: totalRecords,
              totalRecords,
              totalAmount: totalExpected,
              totalExpected,
              totalPaid: totalCollected,
              totalCollected,
              balance: totalPending,
              totalPending,
              paid: fullyPaidCount,
              fullyPaidCount,
              partial: partiallyPaidCount,
              partiallyPaidCount,
              pending: unpaidCount, // mapping unpaid to pending for backward compat if needed, or use separate
              unpaidCount,
              overdue: overdueCount,
              overdueCount,
              collectionRate: Math.round(collectionRate)
            };
          } else {
            // Data is already aggregated
            statsData = res.data || {};
          }

          setFeeStats(statsData);
        }
      } catch (e) {
         // Fallback/Simulated stats if API fails
         const totalRecords = studentFeeRecords.length;
         const totalPaid = studentFeeRecords.filter(r => r.balance === 0).length;
         const totalAmount = studentFeeRecords.reduce((sum, r) => sum + (r.totalAmount || 0), 0);
         const totalPaidAmount = studentFeeRecords.reduce((sum, r) => sum + (r.totalPaid || 0), 0);
         const totalBalance = totalAmount - totalPaidAmount;

         setFeeStats(prev => ({ 
             ...prev, 
             total: totalRecords,
             paid: totalPaid,
             totalAmount: totalAmount,
             totalPaid: totalPaidAmount,
             balance: totalBalance,
         }));

      } finally {
        setStatsLoading(false);
      }
    };

  const loadTimeline = async () => {
    if (!organization?._id) return;
    setTimelineLoading(true);
    try {
      const today = new Date();
      const start = new Date();
      start.setDate(today.getDate() - 30);
      
      const params = {
        org_id: organization._id,
        start_date: start.toISOString().split('T')[0],
        end_date: today.toISOString().split('T')[0],
        granularity: 'day',
        template_name: 'attendance_alert' // Using requested template name
      };
      
      const res = await feeApi.getTimelineAnalytics(params);
      if (res) {
          // Determine data structure, API might return array directly or object
          setTimelineData(Array.isArray(res) ? res : (res.data || []));
      }
    } catch (e) {
      console.error("Failed to load timeline", e);
    } finally {
      setTimelineLoading(false);
    }
  };

  const loadCampaignStats = async () => {
    if (!organization?._id) return;
    setCampaignLoading(true);
    try {
        const res = await whatsappAnalyticsApi.getLast30Days();
        if (res.success && res.data) {
            setCampaignStats(res.data);
        }
    } catch (e) {
        console.error("Failed to load campaign stats", e);
    } finally {
        setCampaignLoading(false);
    }
  };
    
  // Debounce search effect
  useEffect(() => {
    const timer = setTimeout(() => {
        setDebouncedSearch(classFilters.search);
    }, 600);
    return () => clearTimeout(timer);
  }, [classFilters.search]);

  // Load records when filters change (debounced search, status, class) or when switching to records tab
  useEffect(() => {
    if (activeSection === 'recordsByClass') {
      loadFeeRecords();
    }
  }, [currentAcademicYear, debouncedSearch, classFilters.status, classFilters.classId, activeSection]);

  // Refresh structures when entering structures tab or on initial load
  useEffect(() => {
    if (activeSection === 'structures' || activeSection === 'overview') {
      loadFeeStructures();
    }
  }, [currentAcademicYear, activeSection]);

  // Refresh stats when entering overview tab or on initial load
  useEffect(() => {
    if (activeSection === 'overview') {
      loadStats();
      loadTimeline();
      loadCampaignStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAcademicYear, activeSection, organization]);

  // Handlers
  const handleRefreshOverdue = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      await feeApi.refreshOverdueFees(today); 
      toast.success('Overdue statuses refreshed');
      await loadFeeRecords();
      await loadStats();
    } catch (e) {
      toast.error(e.message || 'Failed to refresh overdue');
    }
  };



  const handleSendReminders = (structureId) => {
    setRemindingStructureId(structureId);
    setShowReminderConfirm(true);
  };

  const confirmSendReminders = async () => {
    if (!remindingStructureId) return;
    
    setSendingReminders(remindingStructureId);
    setShowReminderConfirm(false);
    
    try {
      const response = await feeApi.sendDirectReminder(remindingStructureId, targetAudience);
      if (response.success) {
        toast.success(response.message || `Sent ${response.sent} of ${response.total} fee reminders`);
      } else {
        toast.error(response.message || 'Failed to send reminders');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to send fee reminders');
    } finally {
      setSendingReminders(null);
      setRemindingStructureId(null);
    }
  };

  const handleDeleteStructure = (structureId) => {
    setStructureToDelete(structureId);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteStructure = async () => {
    if (!structureToDelete) return;
    try {
      const res = await feeApi.deleteFeeStructure(structureToDelete);
      if (res.success) {
        toast.success('Fee structure deleted');
        await loadFeeStructures();
      } else {
        toast.error(res.message || 'Failed to delete structure');
      }
    } catch (e) {
      toast.error(e.message || 'Failed to delete structure');
    } finally {
      setShowDeleteConfirm(false);
      setStructureToDelete(null);
    }
  };

  const handleEditStructure = (structure) => {
    setEditingStructure(structure);
    setShowStructureForm(true);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-base font-bold text-gray-900">Fees Management</h1>
          <p className="text-[10px] text-gray-500">Manage fee structures, student records, and payments</p>
        </div>
        <div className="flex items-center gap-2">
          {activeSection !== 'structures' && (
            <button onClick={handleRefreshOverdue} className="px-2 py-1 border border-gray-300 rounded text-[10px] font-medium text-gray-700 bg-white hover:bg-gray-50 flex items-center space-x-1 transition-colors">
              <CalendarDays className="h-3 w-3" />
              <span>Refresh Overdue</span>
            </button>
          )}
          {activeSection === 'structures' && (
            <button onClick={() => { setEditingStructure(null); setShowStructureForm(true); }} className="btn-primary text-[10px] flex items-center space-x-1">
              <Plus className="h-3 w-3" />
              <span>New Structure</span>
            </button>
          )}
        </div>
      </div>

      {/* Section switcher */}
      <div className="bg-white border-b border-gray-200">
        <nav className="-mb-px flex flex-wrap gap-2 px-1" aria-label="Sections">
          {[
            { key: 'overview', label: 'Overview' },
            { key: 'structures', label: 'Fee Structures' },
            { key: 'recordsByClass', label: 'Student Records' },

            { key: 'payments', label: 'Payments' },
            { key: 'reminders', label: 'Reminders' },
          ].map(tab => (
            <button
              key={tab.key}
              className="px-3 py-1.5 border-b-2 text-[10px] font-bold uppercase tracking-wider transition-colors border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              style={activeSection === tab.key ? {
                borderColor: 'var(--primary-color, #4f46e5)',
                color: 'var(--primary-color, #4f46e5)'
              } : {}}
              onClick={() => setActiveSection(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {activeSection === 'overview' && (
        <FeeStats 
          stats={feeStats} 
          loading={statsLoading} 
          campaignStats={campaignStats}
          campaignLoading={campaignLoading}
          formatCurrency={formatCurrency} 
          onRefreshOverdue={handleRefreshOverdue} 
        />
      )}

      {activeSection === 'recordsByClass' && (
        <FeeRecords
          classes={allClasses}
          students={students}
          feeRecords={studentFeeRecords}
          loading={recordsLoading}
          expandedClasses={expandedClasses}
          setExpandedClasses={setExpandedClasses}
          filters={classFilters}
          setFilters={setClassFilters}
          formatCurrency={formatCurrency}
          getCategoryStatus={getCategoryStatus}
          onAssignStructure={(idOrEvent) => {
            const classId = typeof idOrEvent === 'string' ? idOrEvent : null;
            setPreSelectedClassId(classId);
            setShowAssignModal(true);
          }}
          onExpandClass={(classId) => fetchStudentsForClasses([classId])}
        />
      )}

      {activeSection === 'structures' && (
        <FeeStructureList
          structures={feeStructures}
          loading={structuresLoading}
          allClasses={allClasses}
          formatCurrency={formatCurrency}
          onEdit={handleEditStructure}

          onDelete={handleDeleteStructure}
          onCreate={() => { setEditingStructure(null); setShowStructureForm(true); }}
          onSendReminders={handleSendReminders}
          sendingReminders={sendingReminders}
        />
      )}

      {activeSection === 'payments' && (
        <div className="bg-white p-3 rounded shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center">
              <div className="p-1 bg-green-100 rounded"><DollarSign className="h-3.5 w-3.5 text-green-600"/></div>
              <h2 className="ml-2 text-xs font-bold text-gray-900">Payment Transactions</h2>
            </div>
          </div>
          <div className="text-center py-6 text-gray-500">
            <h3 className="text-xs font-medium mb-1">Payment Transaction Summary</h3>
            <p className="mb-2 text-[10px]">Total payments, daily/weekly trends, breakdown by mode (UPI, Card, NetBanking), and payment gateway analysis will appear here.</p>
            <button className="btn-primary text-[10px] flex items-center space-x-1 mx-auto">
              <Search className="h-3 w-3" />
              <span>View All Transactions</span>
            </button>
          </div>
        </div>
      )}

      {activeSection === 'reminders' && (
        <div className="bg-white p-3 rounded shadow-sm border border-gray-200">
          <div className="flex items-center mb-2">
              <div className="p-1 bg-yellow-100 rounded"><Send className="h-3.5 w-3.5 text-yellow-600"/></div>
              <h2 className="ml-2 text-xs font-bold text-gray-900">Fee Reminders & Notifications</h2>
          </div>
          <FeeReminders feeStructures={feeStructures} />
        </div>
      )}

      {/* Fee Structure Form Modal */}
      {showStructureForm && (
        <FeeStructureModal
          structure={editingStructure}
          onClose={() => {
            setShowStructureForm(false);
            setEditingStructure(null);
          }}
          onSubmit={async (data) => {
            try {
              // Transform form data to match backend DTO format
              const formattedData = {
                name: data.name,
                academicYear: data.academicYear,
                description: data.description || '',
                categories: data.categories.map(cat => ({
                  name: cat.name,
                  amount: parseFloat(cat.amount) || 0,
                  dueDate: cat.dueDate ? new Date(cat.dueDate).toISOString() : null,
                  isMandatory: cat.isMandatory !== undefined ? cat.isMandatory : true
                })),
                applicableClassIds: data.applicableClassIds || [],
                fineRules: data.fineRules?.map(rule => ({
                  daysAfterDue: parseInt(rule.daysAfterDue) || 0,
                  fineType: rule.fineType,
                  amount: parseFloat(rule.amount) || 0,
                  frequency: rule.frequency
                })) || []
              };

              if (editingStructure) {
                const res = await feeApi.updateFeeStructure(editingStructure._id, formattedData);
                if (res.success) {
                  toast.success('Fee structure updated successfully');
                  setShowStructureForm(false);
                  setEditingStructure(null);
                  await loadFeeStructures();
                } else {
                  toast.error(res.message || 'Failed to update fee structure');
                }
              } else {
                const res = await feeApi.createFeeStructure(formattedData);
                if (res.success) {
                  toast.success('Fee structure created successfully');
                  setShowStructureForm(false);
                  await loadFeeStructures();
                } else {
                  toast.error(res.message || 'Failed to create fee structure');
                }
              }
            } catch (e) {
              toast.error(e.message || editingStructure ? 'Failed to update fee structure' : 'Failed to create fee structure');
            }
          }}
          allClasses={allClasses}
          currentAcademicYear={currentAcademicYear}
        />
      )}
      
      {/* Assign Fee Structure Modal */}
      {showAssignModal && (
        <AssignFeeStructureModal
            feeStructures={feeStructures}
            onClose={() => setShowAssignModal(false)}
            onSubmit={async (data) => {
                try {
                    const res = await feeApi.assignStudentsToFeeStructure(data);
                    if (res.success) {
                        toast.success('Fee structure assigned successfully');
                        setShowAssignModal(false);
                        await loadFeeRecords();
                    } else {
                        toast.error(res.message || 'Failed to assign fee structure');
                    }
                } catch (e) {
                    toast.error(e.message || 'Failed to assign fee structure');
                }
            }}
            students={students}
            allClasses={allClasses}
            feeRecords={studentFeeRecords}
            fetchStudentsForClasses={fetchStudentsForClasses}
            preSelectedClassId={preSelectedClassId}
        />
      )}
      
      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={confirmDeleteStructure}
        title="Delete Fee Structure"
        message="Are you sure you want to delete this fee structure? This action cannot be undone and will affect all assigned students."
        confirmText="Delete"
        confirmButtonClass="bg-red-600 hover:bg-red-700"
      />

      {/* Send Reminders Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showReminderConfirm}
        onClose={() => setShowReminderConfirm(false)}
        onConfirm={confirmSendReminders}
        title="Send Fee Reminders"
        message={`This will send WhatsApp reminders for this fee structure to the selected audience.`}
        confirmText="Send Now"
      >
        <div className="mt-4 mb-2">
           <RecipientSelector
             value={targetAudience}
             onChange={setTargetAudience}
             label="Recipient Audience"
             helperText="Choose who should receive the fee reminders"
           />
        </div>
      </ConfirmDialog>
    </div>
  );
};

import FeeStructureModal from './components/FeeStructureModal';
import AssignFeeStructureModal from './components/AssignFeeStructureModal';


export default Fees;

